import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ColorRing } from 'react-loader-spinner';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { simpleLineChart } from './chartOptions';

import useGetMCTDetailChartData from 'hooks/Traps/Trap/useGetTrapDetailChartData';
import { TrapType } from 'components/AnalyticsPage/TrapType';

interface ChartCardProps {
  slug: string;
  type: TrapType;
  isBarChart?: boolean;
}

interface ChartData {
  date: string[];
  count_approved_mice: number[];
  count_approved_not_mice: number[];
  count_empty?: number[];
}

interface DataItem {
  date: string;
  count_approved_mice: number;
  count_approved_not_mice: number;
  count_empty: number;
}

const SimpleChartCard: React.FC<ChartCardProps> = ({ slug, type, isBarChart = false }) => {
  const { t } = useTranslation();
  const { data, refetch, isLoading } = useGetMCTDetailChartData(slug, type);
  const [chartData, setChartData] = useState<ChartData>({
    date: [],
    count_approved_mice: [],
    count_approved_not_mice: [],
    count_empty: [],
  });

  const chartOptions = simpleLineChart(chartData, t, isBarChart, type === TrapType.SRST);

  useEffect(() => {
    refetch();
  }, [slug, refetch]);

  useEffect(() => {
    if (data) {
      // Group data by day using local timezone
      const groupedByDay = data.reduce((acc: Record<string, DataItem>, item: any) => {
        // Use dayjs to parse ISO string and convert to local timezone
        const dayKey = dayjs(item.date).format('DD-MM-YYYY');

        if (!acc[dayKey]) {
          acc[dayKey] = {
            date: dayKey,
            count_approved_mice: 0,
            count_approved_not_mice: 0,
            count_empty: 0,
          };
        }

        acc[dayKey].count_approved_mice += item.count_approved_mice;
        acc[dayKey].count_approved_not_mice += item.count_approved_not_mice;
        acc[dayKey].count_empty += item.count_empty;
        return acc;
      }, {});

      const sortedData = Object.values(groupedByDay).sort(
        (a, b) => dayjs(a.date, 'DD-MM-YYYY').valueOf() - dayjs(b.date, 'DD-MM-YYYY').valueOf(),
      );

      setChartData({
        date: sortedData
          .map(item => {
            // Parse DD-MM-YYYY format and convert to ISO string
            const [day, month, year] = item.date.split('-');
            const d = dayjs(`${year}-${month}-${day}`);
            return d.isValid() ? d.toISOString() : '';
          })
          .filter(date => date !== ''),
        count_approved_mice: sortedData.map(item => item.count_approved_mice),
        count_approved_not_mice: sortedData.map(item => item.count_approved_not_mice),
        count_empty: sortedData.map(item => item.count_empty),
      });
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="card d-flex m-0">
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: '350px' }}>
          <ColorRing
            visible
            height="80"
            width="80"
            ariaLabel="color-ring-loading"
            wrapperStyle={{}}
            wrapperClass="color-ring-wrapper"
            colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="card-body">
      <ReactApexChart
        key={isBarChart ? 'bar' : 'area'}
        options={chartOptions}
        series={chartOptions.series}
        type={isBarChart ? 'bar' : 'area'}
        height={440}
      />
    </div>
  );
};

export default SimpleChartCard;
